import type { ComputedRef } from 'vue';
import { useTimestamp } from '@vueuse/core';
import { computed } from 'vue';
import { MILLISECONDS_PER_HOUR } from '#layers/base/constants/time';
import { useSystemInfo } from '#layers/base/stores/system-info';

/**
 * A live-ticking clock corrected for browser↔server skew, in unix **seconds**.
 *
 * `useSystemInfo` captures `serverTimeOffsetMs` at each successful `/system/info`
 * fetch; adding it to the ticking browser clock yields the authoritative server
 * time while still decrementing countdowns live. When the offset is `0`
 * (unauthenticated / pre-probe) this is just the browser clock.
 *
 * `useTimestamp` gets an explicit interval because its vueuse default is
 * per-frame (`requestAnimationFrame`), which we don't want for an hourly clock.
 */
export function useServerNow(): ComputedRef<number> {
	const systemInfo = useSystemInfo();
	const timestamp = useTimestamp({ interval: MILLISECONDS_PER_HOUR });

	return computed(() => Math.floor((timestamp.value + systemInfo.serverTimeOffsetMs) / 1000));
}
